import { FC, useMemo } from "react"
import { Activity } from "lucide-react"
import { BBColumnDef, Integration, IntegrationStatus } from "../../../../types"
import If from "../../if"
import Table from "../../table"
import { formatDateTimeFromTimestamp } from "../../../utils"
import { useGetEmbedStatsOrgQuery } from "../../../../redux/queries"
import { useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import Skeleton from "../../skeletons/base"

interface Props {
  integration?: Integration
  statusData?: IntegrationStatus[]
  isLoading: boolean
}

const MetricCard: FC<{
  label: string
  value: string | number
  loading?: boolean
}> = ({ label, value, loading }) => (
  <div className="bg-white rounded shadow-sm p-6">
    <div className="text-xs font-semibold uppercase tracking-widest text-neutral-400 mb-1.5">
      {label}
    </div>
    {loading ? (
      <div className="h-8 w-24 animate-pulse rounded bg-gray-200" />
    ) : (
      <div className="text-3xl font-extrabold text-neutral-900 leading-none">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    )}
  </div>
)

const Status: FC<Props> = ({ integration, statusData = [], isLoading }) => {
  const userDetails = useAppSelector(selectUserDetails)
  const orgId = userDetails?.org?.id

  const { data: statsData7d = [], isLoading: isLoading7d } =
    useGetEmbedStatsOrgQuery(
      { org_id: Number(orgId!), range: 7 },
      { skip: !orgId || !integration?.id }
    )

  const { data: statsData30d = [], isLoading: isLoading30d } =
    useGetEmbedStatsOrgQuery(
      { org_id: Number(orgId!), range: 30 },
      { skip: !orgId || !integration?.id }
    )

  const { data: statsData90d = [], isLoading: isLoading90d } =
    useGetEmbedStatsOrgQuery(
      { org_id: Number(orgId!), range: 90 },
      { skip: !orgId || !integration?.id }
    )

  const isLoadingPageviews = isLoading7d || isLoading30d || isLoading90d

  const pageviews7d = useMemo(() => {
    const row = statsData7d.find(r => String(r.id) === integration?.id)
    if (!row) return 0
    const requests =
      typeof row.requests === "string"
        ? parseInt(row.requests, 10)
        : row.requests
    return isNaN(requests) ? 0 : requests
  }, [statsData7d, integration?.id])

  const pageviews30d = useMemo(() => {
    const row = statsData30d.find(r => String(r.id) === integration?.id)
    if (!row) return 0
    const requests =
      typeof row.requests === "string"
        ? parseInt(row.requests, 10)
        : row.requests
    return isNaN(requests) ? 0 : requests
  }, [statsData30d, integration?.id])

  const pageviews90d = useMemo(() => {
    const row = statsData90d.find(r => String(r.id) === integration?.id)
    if (!row) return 0
    const requests =
      typeof row.requests === "string"
        ? parseInt(row.requests, 10)
        : row.requests
    return isNaN(requests) ? 0 : requests
  }, [statsData90d, integration?.id])

  const columns: BBColumnDef<IntegrationStatus>[] = [
    {
      id: "timestamp",
      header: "Date/Time",
      accessorFn: row => row.timestamp,
      footer: props => props.column.id,
      cell: props => (
        <td
          className={`px-2 py-4 ${
            props.row.original.validated === "1" ? "text-primary-400" : ""
          }`}
        >
          {formatDateTimeFromTimestamp(
            props.getValue() as string,
            "LL/dd/yyyy h:mm:ss a"
          )}
        </td>
      )
    },
    {
      id: "referrer",
      header: "Referrer",
      accessorFn: row => row.referrer,
      footer: props => props.column.id,
      cell: props => (
        <td
          className={`pl-3 py-4 ${
            props.row.original.validated === "1" ? "text-primary-400" : ""
          }`}
          colSpan={2}
        >
          {props.getValue() as string}
        </td>
      ),
      meta: { headerClassName: "hidden md:table-cell" }
    }
  ]

  return (
    <div className="flex flex-col gap-5">
      {/* Metric cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="7 Day Pageviews"
          value={pageviews7d}
          loading={isLoadingPageviews}
        />
        <MetricCard
          label="30 Day Pageviews"
          value={pageviews30d}
          loading={isLoadingPageviews}
        />
        <MetricCard
          label="90 Day Pageviews"
          value={pageviews90d}
          loading={isLoadingPageviews}
        />
      </div>

      {/* Activity log */}
      <div className="bg-white rounded shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <Activity size={15} className="text-violet-400" />
          <span className="text-sm font-medium text-neutral-800">
            Recent Activity Log
          </span>
        </div>

        <If
          isLoading={isLoading}
          condition={statusData?.length > 0}
          then={
            <Table
              columns={columns}
              data={statusData}
              viewType="table"
              pageSize={25}
              searchBarVisible={false}
            />
          }
          else={
            <div className="py-6 text-sm text-neutral-400">
              No activity data available.
            </div>
          }
        />
      </div>
    </div>
  )
}

export default Status
